import React, { useState, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import filesService from "../../services/filesService";
import PageHeader from "../shared/PageHeader";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Snackbar,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  FileText,
  Lightbulb,
  GraduationCap,
  Building2,
  User,
  List as ListIcon,
  FolderOpen,
  Upload,
  CheckCircle,
  X,
  Settings,
  Tag,
  Info,
  Minus,
} from "lucide-react";
import styles from "./KnowledgeBase.module.scss";
import hubStyles from "../KnowledgeBaseHub/KnowledgeBaseHub.module.scss";
import ManualTagDialog from "../AgentCreate/ManualTagDialog";
import { AssistantDoneBanner, useAssistantReturn } from "../Assistant/assistantReturn";

const KnowledgeBase = () => {
  const { isDarkMode } = useTheme();
  const { folderId } = useParams();
  const navigate = useNavigate();
  // Arrived from Claude / Town: end on "go back to your chat", not the folder.
  const assistantReturn = useAssistantReturn();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showManualTagDialog, setShowManualTagDialog] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [actualFiles, setActualFiles] = useState(new Map()); // Store actual File objects
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef(null);

  // All knowledge source categories in one array
  const allCategories = [
    {
      id: "standard-procedures",
      title: "Standard Procedures",
      description: "Step-by-step guides and compliance documents",
      icon: <FileText size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#f44336",
      processingRules: {
        vectorized: true,
        summarized: false,
        preserved: true,
      },
      note: "Vectorized for search and preserved for compliance",
      tags: [
        { label: "SOP", color: "#f44336" },
        { label: "POLICY", color: "#9c27b0" },
      ],
      examples: [
        "Employee handbook",
        "Process documentation",
        "Standard operating procedures",
      ],
    },
    {
      id: "best-practices",
      title: "Best Practices & Templates",
      description: "Proven methods, recommendations, and templates",
      icon: <Lightbulb size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#2196f3",
      processingRules: {
        vectorized: true,
        summarized: false,
        preserved: true,
      },
      note: "Vectorized for search but preserved for reference",
      tags: [
        { label: "BP", color: "#2196f3", icon: "star" },
        { label: "TEMPLATE", color: "#757575", icon: "document" },
        { label: "ADV", color: "#ff9800", icon: "lightbulb" },
      ],
      examples: [
        "Industry best practices",
        "Templates",
        "Guidelines",
        "Advisory content",
      ],
    },
    {
      id: "training-materials",
      title: "Training & Learning Materials",
      description: "Educational content and learning resources",
      icon: <GraduationCap size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#4caf50",
      processingRules: {
        vectorized: true,
        summarized: true,
        preserved: false,
      },
      note: "Vectorized and summarized for efficient learning",
      tags: [
        { label: "TRAINING", color: "#4caf50", icon: "school" },
        { label: "CALL_LOG", color: "#f44336", icon: "phone" },
      ],
      examples: [
        "Training manuals",
        "Educational content",
        "Call transcripts",
        "Learning resources",
      ],
    },
    {
      id: "business-organizational",
      title: "Business & Organizational",
      description: "Company-specific information and organizational data",
      icon: <Building2 size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#9c27b0",
      processingRules: {
        vectorized: true,
        summarized: false,
        preserved: true,
      },
      note: "Vectorized for analysis, others preserved",
      tags: [
        { label: "ORG", color: "#2196f3", icon: "business" },
        { label: "TRADE_SECRET", color: "#e91e63", icon: "lock" },
        { label: "DELIVERY", color: "#ff9800", icon: "box" },
      ],
      examples: [
        "Company information",
        "Internal processes",
        "Proprietary data",
        "Delivery templates",
      ],
    },
    {
      id: "customer-external",
      title: "Customer & External Data",
      description: "Customer profiles, personas, and external information",
      icon: <User size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#e91e63",
      processingRules: {
        vectorized: true,
        summarized: false,
        preserved: true,
      },
      note: "Vectorized for analysis, selectively preserved",
      tags: [
        { label: "PERSONA", color: "#ffeb3b" },
        { label: "TP", color: "#ff9800" },
        { label: "TLD", color: "#9c27b0" },
      ],
      examples: [
        "Customer profiles",
        "User personas",
        "Third-party data",
        "Team discussions",
      ],
    },
    {
      id: "specialized-niche",
      title: "Specialized & Niche Content",
      description: "Industry-specific and specialized information",
      icon: <ListIcon size={24} color="white" strokeWidth={2} aria-hidden />,
      iconBg: "#2196f3",
      processingRules: {
        vectorized: true,
        summarized: false,
        preserved: true,
      },
      note: "Specialized content vectorized and preserved",
      tags: [
        { label: "NICHE", color: "#2196f3" },
        { label: "DNS", color: "#f44336" },
      ],
      examples: [
        "Technical documentation",
        "Industry reports",
        "Specialized knowledge",
        "Protected content",
      ],
    },
  ];

  // Auto-detect tags using service
  const autoDetectTags = (fileName) => {
    return filesService.autoDetectTags(fileName);
  };

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0 && !isProcessingUpload && !isUploading && !isProcessingFile) {
      const file = files[0];

      console.log('File selected via input:', file.name);

      // Set global processing flag
      setIsProcessingFile(true);

      // Auto-detect tags
      const autoDetected = autoDetectTags(file.name);
      console.log('Auto-detected tags:', autoDetected);

      // Set processing flag to prevent duplicate uploads
      setIsProcessingUpload(true);

      // Set the uploading file first
      setUploadingFile(file);

      // If we detected a primary tag, auto-upload without dialog
      if (autoDetected.primaryTag) {
        console.log('Auto-detected primary tag, proceeding with upload');
        handleAutoUpload(file, autoDetected);
      } else {
        // No auto-detection, show manual dialog
        setShowManualTagDialog(true);
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;

    if (files.length > 0 && !isProcessingUpload && !isUploading && !isProcessingFile) {
      const file = files[0];

      console.log('File dropped:', file.name);

      // Set global processing flag
      setIsProcessingFile(true);

      // Auto-detect tags
      const autoDetected = autoDetectTags(file.name);
      console.log('Auto-detected tags:', autoDetected);

      // Set processing flag to prevent duplicate uploads
      setIsProcessingUpload(true);

      // Set the uploading file first
      setUploadingFile(file);

      // If we detected a primary tag, auto-upload without dialog
      if (autoDetected.primaryTag) {
        console.log('Auto-detected primary tag, proceeding with upload');
        handleAutoUpload(file, autoDetected);
      } else {
        // No auto-detection, show manual dialog
        setShowManualTagDialog(true);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const getStatusIcon = (status) => {
    if (status === true) return <CheckCircle size={16} strokeWidth={2} color="#16a34a" aria-hidden />;
    if (status === false) return <X size={16} strokeWidth={2} color="#dc2626" aria-hidden />;
    return null;
  };

  const getTagIcon = (iconType) => {
    switch (iconType) {
      case "star": return "⭐";
      case "document": return "📄";
      case "lightbulb": return "💡";
      case "school": return "🎓";
      case "phone": return "📞";
      case "business": return "🏢";
      case "lock": return "🔒";
      case "box": return "📦";
      default: return null;
    }
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const handleAutoUpload = (file, autoDetected) => {
    // Prevent multiple calls
    if (isUploading || !file) {
      console.log('Preventing duplicate auto-upload - already uploading or no file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Show auto-detection notification
    showToast(`Auto-detected tags: ${autoDetected.primaryTag}${autoDetected.secondaryTags.length > 0 ? ` + ${autoDetected.secondaryTags.join(', ')}` : ''}`, 'success');

    // Clear the file input to prevent duplicate uploads
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);

          // Add file to uploaded files list
          const fileId = Date.now();
          const newFile = {
            id: fileId,
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB',
            primaryTag: autoDetected.primaryTag,
            secondaryTags: autoDetected.secondaryTags,
            processingStatus: getProcessingStatus(autoDetected.primaryTag),
            uploadDate: new Date().toLocaleDateString(),
            status: 'New',
            autoDetected: true // Flag to show it was auto-detected
          };

          setUploadedFiles(prev => {
            // Check if this exact file is already being processed
            const isAlreadyProcessing = prev.some(existingFile => 
              existingFile.name === newFile.name && 
              existingFile.size === newFile.size &&
              existingFile.primaryTag === newFile.primaryTag &&
              existingFile.autoDetected === true
            );

            if (isAlreadyProcessing) {
              console.log('File already being processed, skipping duplicate auto-upload');
              return prev;
            }

            console.log('Auto-uploading file:', {
              fileName: newFile.name,
              primaryTag: newFile.primaryTag,
              secondaryTags: newFile.secondaryTags
            });

            return [...prev, newFile];
          });

          // Store the actual File object
          setActualFiles(prev => {
            const newMap = new Map(prev);
            newMap.set(fileId, file);
            return newMap;
          });

          setUploadingFile(null);
          setUploadProgress(0);
          setIsProcessingUpload(false);
          setIsProcessingFile(false); // Reset global processing flag
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleManualTagConfirm = (tagData) => {
    // Prevent multiple calls
    if (isUploading || !uploadingFile) {
      console.log('Preventing duplicate upload - already uploading or no file');
      return;
    }

    setShowManualTagDialog(false);
    setIsUploading(true);
    setUploadProgress(0);

    // Clear the file input to prevent duplicate uploads
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Store the current file to prevent race conditions
    const currentFile = uploadingFile;

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);

          // Add file to uploaded files list only once
          const fileId = Date.now();
          const newFile = {
            id: fileId,
            name: currentFile.name,
            size: (currentFile.size / 1024).toFixed(1) + ' KB',
            primaryTag: tagData.primaryTag,
            secondaryTags: tagData.secondaryTags,
            processingStatus: getProcessingStatus(tagData.primaryTag),
            uploadDate: new Date().toLocaleDateString(),
            status: 'New',
          };

          setUploadedFiles(prev => {
            // Check if this exact file is already being processed
            const isAlreadyProcessing = prev.some(file => 
              file.name === newFile.name && 
              file.size === newFile.size &&
              file.primaryTag === newFile.primaryTag
            );

            if (isAlreadyProcessing) {
              console.log('File already being processed, skipping duplicate');
              return prev;
            }

            console.log('Adding new file:', {
              newFileName: newFile.name,
              newFileSize: newFile.size,
              allFiles: prev.map(f => ({ name: f.name, size: f.size }))
            });

            return [...prev, newFile];
          });

          // Store the actual File object
          setActualFiles(prev => {
            const newMap = new Map(prev);
            newMap.set(fileId, currentFile);
            return newMap;
          });

          setUploadingFile(null);
          setUploadProgress(0);
          setIsProcessingUpload(false); // Reset processing flag
          setIsProcessingFile(false); // Reset global processing flag
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleManualTagCancel = () => {
    setShowManualTagDialog(false);
    setUploadingFile(null);
    setIsProcessingUpload(false); // Reset processing flag
    setIsProcessingFile(false); // Reset global processing flag
  };

  const getProcessingStatus = (primaryTag) => {
    // Define processing rules based on primary tag
    const processingRules = {
      'SOP': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'POLICY': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'TRADE_SECRET': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'BP': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'TEMPLATE': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'ADV': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'TRAINING': { vectorized: true, summarized: true, preserved: false, note: 'Vectorized and summarized for efficient learning' },
      'CALL_LOG': { vectorized: true, summarized: true, preserved: false, note: 'Vectorized and summarized for efficient learning' },
      'TP': { vectorized: true, summarized: false, preserved: false, note: 'Vectorized for analysis, preservation varies by type' },
      'NICHE': { vectorized: true, summarized: false, preserved: false, note: 'Vectorized for analysis, preservation varies by type' },
      'ORG': { vectorized: true, summarized: false, preserved: true, note: 'Organizational content vectorized and preserved' },
      'PERSONA': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for analysis, selectively preserved' },
      'DNS': { vectorized: true, summarized: false, preserved: true, note: 'DNS content vectorized and preserved' },
      'DELIVERY': { vectorized: true, summarized: false, preserved: true, note: 'Delivery content vectorized and preserved' },
      'TLD': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for analysis, selectively preserved' },
    };

    return processingRules[primaryTag] || { vectorized: true, summarized: false, preserved: true, note: 'Processing status unknown' };
  };

  const handleDeleteFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setActualFiles(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
  };

  // Function to remove duplicate files (can be called if needed)
  const removeDuplicateFiles = () => {
    setUploadedFiles(prev => {
      const uniqueFiles = [];
      const seen = new Set();

      prev.forEach(file => {
        const key = `${file.name}-${file.size}-${file.primaryTag}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueFiles.push(file);
        }
      });

      return uniqueFiles;
    });
  };

  // Function to clear all files (for testing)
  const clearAllFiles = () => {
    setUploadedFiles([]);
    setActualFiles(new Map());
  };

  // Function to save all files to server
  const handleSaveToServer = async () => {
    if (uploadedFiles.length === 0) {
      showToast('No files to save', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Validate folder ID
      if (!folderId) {
        showToast('No folder specified for upload', 'error');
        return;
      }

      // Prepare request metadata in JSON format
      const requestMetadata = {
        request_type: "d1_knowledge_base_upload",
        additional_data: {
          folder_id: Number(folderId),
          files_metadata: uploadedFiles.map(file => ({
            folder_id: Number(folderId),
            file_id: file.id,
            file_name: file.name,
            file_size: file.size,
            primary_tag: file.primaryTag,
            secondary_tags: file.secondaryTags.join(','),
            vectorized: file.processingStatus.vectorized,
            summarized: file.processingStatus.summarized,
            preserved: file.processingStatus.preserved,
          }))
        }
      };

      // Add request_json field
      formData.append('request_json', JSON.stringify(requestMetadata));

      // Add actual File objects to FormData
      uploadedFiles.forEach(file => {
        const actualFile = actualFiles.get(file.id);
        if (actualFile) {
          // Append each file with its metadata as a separate field
          formData.append('files', actualFile);
        }
      });

      console.log('Sending FormData to server:', {
        request_json: requestMetadata,
        files_count: uploadedFiles.length,
        actual_files: Array.from(actualFiles.keys()),
        formData_entries: Array.from(formData.entries()).map(([key, value]) => ({
          key,
          value: value instanceof File ? `File: ${value.name}` : value
        }))
      });

      // Make API call
      const result = await filesService.uploadFiles(formData);
      console.log('Server response:', result);

      if (assistantReturn.fromAssistant) {
        assistantReturn.markDone();
        showToast(`Successfully saved ${uploadedFiles.length} files to server!`, 'success');
        return;
      }

      showToast(`Successfully saved ${uploadedFiles.length} files to server! Redirecting to folder view...`, 'success');

      // Redirect back to folder files view after successful upload
      if (folderId) {
        setTimeout(() => {
          navigate(`/knowledge-base/folder/${folderId}`);
        }, 2000); // Wait 2 seconds to show the success message
      }

      // Optionally clear the uploaded files after successful save
      // setUploadedFiles([]);
      // setActualFiles(new Map());

    } catch (error) {
      console.error('Error saving files to server:', error);
      showToast('Failed to save files to server. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!folderId) {
    return <Navigate to="/knowledge-hub?tab=d1" replace />;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.knowledgeBase}>
        {/* Header */}
        <PageHeader
          title="Rules book"
          subtitle="Choose Your AI's Knowledge Sources - Select the types of information your AI agent can access and use"
        />

        <Box className={styles.content}>

        {/* Integrated D1 Knowledge Management Banner */}
        <Alert 
          severity="info" 
          className={styles.infoBanner}
          icon={<Info size={22} strokeWidth={2} aria-hidden />}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            Integrated D1 Knowledge Management: Configure knowledge types and upload files directly during agent creation. Your AI will automatically process files according to 4AY rules:
          </Typography>
          <Box className={styles.ruleButtons}>
            <Chip 
              label="Auto-tagging from filenames" 
              size="small" 
              icon={<Box>🔍</Box>}
              className={styles.ruleChip}
            />
            <Chip 
              label="Smart vectorization" 
              size="small" 
              icon={<Box>⚡</Box>}
              className={styles.ruleChip}
            />
            <Chip 
              label="Intelligent summarization" 
              size="small" 
              icon={<Box>📄</Box>}
              className={styles.ruleChip}
            />
            <Chip 
              label="Compliance preservation" 
              size="small" 
              icon={<Box>🔒</Box>}
              className={styles.ruleChip}
            />
            <Chip 
              label="Direct file upload" 
              size="small" 
              icon={<Upload size={18} strokeWidth={2} aria-hidden />}
              className={styles.ruleChip}
            />
          </Box>
        </Alert>

        {/* All Knowledge Categories Grid */}
        <Box className={styles.categoriesGrid}>
          {allCategories.map((category) => (
            <Box key={category.id} className={styles.gridItem}>
              <Card className={styles.categoryCard}>
                <CardContent>
                  <Box className={styles.categoryHeader}>
                    <Box 
                      className={styles.categoryIcon}
                      sx={{ backgroundColor: category.iconBg }}
                    >
                      {category.icon}
                    </Box>
                    <Box className={styles.categoryInfo}>
                      <Typography variant="h6" className={styles.categoryTitle}>
                        {category.title}
                      </Typography>
                      <Typography variant="body2" className={styles.categoryDescription}>
                        {category.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className={styles.processingRules}>
                    <Box className={styles.rulesHeader}>
                      <Settings size={16} strokeWidth={2} style={{ marginRight: 8, flexShrink: 0 }} aria-hidden />
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        Processing Rules
                      </Typography>
                    </Box>
                    <Box className={styles.rulesList}>
                      <Box className={styles.ruleItem}>
                        <Typography variant="body2">Vectorized:</Typography>
                        {getStatusIcon(category.processingRules.vectorized)}
                      </Box>
                      <Box className={styles.ruleItem}>
                        <Typography variant="body2">Summarized:</Typography>
                        {getStatusIcon(category.processingRules.summarized)}
                      </Box>
                      <Box className={styles.ruleItem}>
                        <Typography variant="body2">Preserved:</Typography>
                        {getStatusIcon(category.processingRules.preserved)}
                      </Box>
                    </Box>
                    <Typography variant="caption" className={styles.ruleNote}>
                      {category.note}
                    </Typography>
                  </Box>

                  <Box className={styles.autoTags}>
                    <Box className={styles.tagsHeader}>
                      <Tag size={16} strokeWidth={2} style={{ marginRight: 8, flexShrink: 0 }} aria-hidden />
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        Auto-detected Tags
                      </Typography>
                    </Box>
                    <Box className={styles.tagsList}>
                      {category.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getTagIcon(tag.icon)}
                              {tag.label}
                            </Box>
                          }
                          size="small"
                          sx={{ 
                            backgroundColor: tag.color,
                            color: "white",
                            fontSize: "0.7rem",
                            height: 24,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box className={styles.examples}>
                    {category.examples.map((example, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        className={styles.exampleButton}
                      >
                        {example}
                      </Button>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* File Upload Section */}
        <div className={`${hubStyles.listCard} ${styles.uploadSection}`}>
          <Box className={styles.uploadHeader}>
            <Upload size={24} strokeWidth={2} style={{ marginRight: 8, flexShrink: 0 }} aria-hidden />
            <Typography variant="h5" className={styles.uploadTitle}>
              Upload D1 Rules book Files
            </Typography>
            {folderId && (
              <button
                type="button"
                className={hubStyles.viewAgentBtn}
                onClick={() => navigate(`/knowledge-base/folder/${folderId}`)}
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <FolderOpen size={18} strokeWidth={2} aria-hidden />
                Back to Folder
              </button>
            )}
          </Box>
          {assistantReturn.done && <AssistantDoneBanner sx={{ mb: 2 }} />}
          <Typography variant="body1" className={styles.uploadDescription}>
            Upload files directly to your AI's permanent knowledge base. Files will be automatically tagged, processed, and made available for all conversations.
          </Typography>

          <Alert 
            severity="success" 
            className={styles.smartProcessingBanner}
            icon={<CheckCircle size={22} strokeWidth={2} aria-hidden />}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Smart Processing Active: All files are automatically vectorized for intelligent search, with summarization and preservation applied based on 4AY rules.
            </Typography>
          </Alert>

          {!folderId ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                No folder specified. Please access this page from a folder's upload option.
              </Typography>
            </Alert>
          ) : (
            <Box
              className={`${styles.uploadArea} ${isDragOver ? styles.dragOver : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              <Upload size={48} strokeWidth={1.5} style={{ color: 'var(--text-secondary)', marginBottom: 16 }} aria-hidden />
              <Typography variant="h6" className={styles.uploadText}>
                {isDragOver ? 'Drop files here!' : 'Upload Rules book Files'}
              </Typography>
              <Typography variant="body2" className={styles.uploadSubtext}>
                {isDragOver ? 'Release to upload' : 'Drag and drop files here, or click to browse'}
              </Typography>

              {isUploading && (
                <Box className={styles.uploadProgress}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ width: "100%", mb: 1 }}
                  />
                  <Typography variant="caption">
                    Uploading... {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="caption" className={styles.supportedFormats}>
            Supported formats: PDF, DOC, DOCX, TXT, MD, XLS, XLSX, PPT, PPTX, Images
          </Typography>
        </div>

        {/* Uploaded Files Section */}
        {uploadedFiles.length > 0 && (
          <div className={`${hubStyles.listCard} ${styles.uploadedFilesSection}`}>
            <Box className={styles.uploadedFilesHeader}>
              <FolderOpen size={24} strokeWidth={2} style={{ marginRight: 8, flexShrink: 0 }} aria-hidden />
              <Typography variant="h5" className={styles.uploadedFilesTitle}>
                Uploaded Files ({uploadedFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={hubStyles.uploadBtn}
                  onClick={handleSaveToServer}
                  disabled={isSaving || uploadedFiles.length === 0}
                  style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                >
                  {isSaving ? 'Saving...' : 'Save to Server'}
                </button>
                {folderId && (
                  <button
                    type="button"
                    className={hubStyles.viewAgentBtn}
                    onClick={() => navigate(`/knowledge-base/folder/${folderId}`)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', padding: '8px 16px' }}
                  >
                    <FolderOpen size={18} strokeWidth={2} aria-hidden />
                    Back to Folder
                  </button>
                )}
                <button
                  type="button"
                  className={hubStyles.viewAgentBtn}
                  onClick={removeDuplicateFiles}
                  style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                >
                  Remove Duplicates
                </button>
                <button
                  type="button"
                  className={hubStyles.viewAgentBtn}
                  onClick={clearAllFiles}
                  style={{ fontSize: '0.8125rem', padding: '8px 16px', color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.45)' }}
                >
                  Clear All
                </button>
              </Box>
            </Box>

            <Box className={styles.filesGrid}>
              {uploadedFiles.map((file) => (
              <Box key={file.id} className={styles.fileCard}>
                <Box className={styles.fileHeader}>
                  <Box className={styles.fileInfo}>
                    <Box className={styles.fileNameRow}>
                      <Typography variant="subtitle1" className={styles.fileName}>
                        {file.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {file.autoDetected && (
                          <Chip 
                            label="Auto" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#4caf50', 
                              color: 'white',
                              fontSize: '0.65rem',
                              height: 18
                            }}
                          />
                        )}
                        <Chip 
                          label={file.status} 
                          size="small" 
                          className={styles.statusChip}
                          color={file.status === 'New' ? 'success' : 'primary'}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" className={styles.fileSize}>
                      {file.size}
                    </Typography>
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteFile(file.id)}
                    className={styles.deleteButton}
                    aria-label="Remove file"
                  >
                    <Minus size={20} strokeWidth={2} />
                  </IconButton>
                </Box>

                <Box className={styles.fileDetails}>
                  <Box className={styles.tagsSection}>
                    <Typography variant="body2" className={styles.tagsLabel}>
                      Auto-detected Tags:
                    </Typography>
                    <Box className={styles.tagsList}>
                      <Chip 
                        label={file.primaryTag} 
                        size="small" 
                        className={styles.primaryTagChip}
                        color="primary"
                      />
                      {file.secondaryTags.map((tag, index) => (
                        <Chip 
                          key={index}
                          label={tag} 
                          size="small" 
                          className={styles.secondaryTagChip}
                          color="secondary"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box className={styles.processingSection}>
                    <Typography variant="body2" className={styles.processingLabel}>
                      Processing Preview:
                    </Typography>
                    <Box className={styles.processingStatus}>
                      <Chip 
                        label={`Vectorized: ${file.processingStatus.vectorized ? '✓' : '✗'}`}
                        size="small"
                        className={styles.processingChip}
                        color={file.processingStatus.vectorized ? 'success' : 'error'}
                      />
                      <Chip 
                        label={`Summarized: ${file.processingStatus.summarized ? '✓' : '✗'}`}
                        size="small"
                        className={styles.processingChip}
                        color={file.processingStatus.summarized ? 'success' : 'error'}
                      />
                      <Chip 
                        label={`Preserved: ${file.processingStatus.preserved ? '✓' : '✗'}`}
                        size="small"
                        className={styles.processingChip}
                        color={file.processingStatus.preserved ? 'success' : 'error'}
                      />
                    </Box>
                    <Typography variant="caption" className={styles.processingNote}>
                      {file.processingStatus.note}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              ))}
            </Box>
          </div>
        )}

        {/* D1 knowledge base Summary */}
        {uploadedFiles.length > 0 && (
          <div className={`${hubStyles.listCard} ${styles.summarySection}`}>
            <Typography variant="h5" className={styles.summaryTitle}>
              D1 Rules book Summary:
            </Typography>
            <Box className={styles.summaryGrid}>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>Total Files:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.length}</Typography>
              </Box>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>New Files:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.filter(f => f.status === 'New').length}</Typography>
              </Box>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>Updated Files:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.filter(f => f.status === 'Updated').length}</Typography>
              </Box>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>Vectorized:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.filter(f => f.processingStatus.vectorized === true).length}</Typography>
              </Box>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>Summarized:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.filter(f => f.processingStatus.summarized === true).length}</Typography>
              </Box>
              <Box className={styles.summaryItem}>
                <Typography variant="body2" className={styles.summaryLabel}>Preserved:</Typography>
                <Typography variant="h6" className={styles.summaryValue}>{uploadedFiles.filter(f => f.processingStatus.preserved === true).length}</Typography>
              </Box>
            </Box>
          </div>
        )}
        </Box>
      </Box>

      {/* Manual Tag Selection Dialog */}
      <ManualTagDialog
        open={showManualTagDialog}
        onClose={handleManualTagCancel}
        onConfirm={handleManualTagConfirm}
        fileName={uploadingFile?.name}
      />


      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleToastClose} 
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default KnowledgeBase;
